import React, { useEffect, useState, useRef } from 'react';
import './MarketMarquee.css';

const MarqueeItem = ({ item, hideTicker = false }) => {
  const isPositive = item.pct_change >= 0;

  // Map mismatched flag filenames to actual files
  const FLAG_FIXES = {
    'usa.svg': 'united_states.svg',
  };
  const flagFile = FLAG_FIXES[item.flag_filename] || item.flag_filename;
  const flagPath = `/assets/flags/${flagFile}`;
  
  let label = item.ticker;
  if (label.startsWith('^')) {
    label = label.substring(1);
  }

  return (
    <div className="mq-item">
      {item.flag_filename && item.tape_group === 'index' && (
        <img src={flagPath} alt="" className="mq-flag" onError={(e) => e.target.style.display='none'} />
      )}
      {!hideTicker && <span className="mq-label">{label}</span>}
      <span className="mq-price">{Number(item.last_price).toLocaleString()}</span>
      <span className={`mq-change ${isPositive ? 'pos' : 'neg'}`}>
        {isPositive ? '▲' : '▼'} {Math.abs(item.pct_change)}%
      </span>
    </div>
  );
};

const MarketMarquee = ({ group = 'index', pixelsPerSecond = 40 }) => {
  const [items, setItems] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(100);
  const trackRef = useRef(null);
  
  const API_URL = process.env.REACT_APP_API_URL
    || (process.env.NODE_ENV === "development" ? `http://${window.location.hostname}:8000` : "");

  useEffect(() => {
    fetch(`${API_URL}/api/market-marquee`)
      .then(res => res.json())
      .then(res => {
        if (res.status === 'success') {
          const filtered = res.data.filter(i => i.tape_group === group);
          filtered.sort((a, b) => a.region.localeCompare(b.region));
          setItems(filtered);
        }
      })
      .catch(err => console.error('Marquee error:', err));
  }, [group, API_URL]);

  // Handle uniform speed by calculating duration based on track width
  useEffect(() => {
    if (trackRef.current && items.length > 0) {
      const trackWidth = trackRef.current.scrollWidth;
      // Because we double the items for looping, total width is trackWidth
      // We want to move half the total width (the original set) in X seconds
      const calculatedDuration = (trackWidth / 2) / pixelsPerSecond;
      setDuration(calculatedDuration);
    }
  }, [items, pixelsPerSecond]);

  if (items.length === 0) return null;

  const renderGroup = (metalItems, label, className, hideTicker = false) => {
    if (metalItems.length === 0) return null;
    return (
      <>
        <div className={`mq-category-label ${className}`}>{label}:</div>
        {metalItems.map((item, idx) => (
          <MarqueeItem key={`${item.ticker}-${idx}`} item={item} hideTicker={hideTicker} />
        ))}
      </>
    );
  };

  const renderMarqueeContent = (data) => {
    if (group === 'index') {
      return (
        <>
          <div className="mq-category-label">Global Indices:</div>
          {data.map((item, idx) => (
            <MarqueeItem key={`${item.ticker}-${idx}`} item={item} />
          ))}
        </>
      );
    } else {
      const gold = data.filter(i => i.ticker === 'XAUUSD');
      const silver = data.filter(i => i.ticker === 'XAGUSD');
      const palladium = data.filter(i => i.ticker === 'XPDUSD');
      const platinum = data.filter(i => i.ticker === 'XPTUSD');
      const exrates = data.filter(i => i.region === 'exrate');
      
      return (
        <>
          {renderGroup(gold, "Gold", "gold", true)}
          {renderGroup(silver, "Silver", "silver", true)}
          {renderGroup(palladium, "Palladium", "palladium", true)}
          {renderGroup(platinum, "Platinum", "platinum", true)}
          {exrates.length > 0 && <div className="mq-category-label">Exchange Rates:</div>}
          {exrates.map((item, idx) => (
            <MarqueeItem key={`${item.ticker}-${idx}`} item={item} />
          ))}
        </>
      );
    }
  };

  return (
    <div 
      className={`mq-container ${isPaused ? 'paused' : ''}`}
      onClick={() => setIsPaused(!isPaused)}
      title="Click to pause/resume"
    >
      <div 
        ref={trackRef}
        className="mq-track" 
        style={{ 
          animationDuration: `${duration}s`,
          animationPlayState: isPaused ? 'paused' : 'running'
        }}
      >
        {renderMarqueeContent(items)}
        <div className="mq-spacer" />
        {renderMarqueeContent(items)}
      </div>
    </div>
  );
};

export default MarketMarquee;
